import { useEffect, useState } from "react";
import { fetchEventsList } from "../../lib/api";
import { toUIEvent, type UIEvent } from "../../lib/events-adapter";
import { logUpsert } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

/**
 * useEvents — the Events list data source, backed by a shared module store.
 *
 * #800 rewrite (2026-07-13 docket §3): data-layer-canonical, no local-wins.
 *  • The forbidden `cd:studio:local-events:v1` localStorage "local-wins" layer
 *    is REMOVED (it violated the no-UI-seed / data-layer-canonical mandate and
 *    made a hard refresh "wipe" rows the server never had).
 *  • Transient fetch errors PRESERVE the last-good list — the catch never
 *    blanks previously-loaded rows; only a first-load failure shows the error
 *    empty state.
 *  • Revalidation on re-nav, refocus + mutation: every hook mount (each screen
 *    navigation) kicks a stale-while-revalidate fetch (in-flight-deduped);
 *    window `focus` + `document.visibilitychange` (plus a light visible-only
 *    poll) reconverge tabs that stayed open while another user/tab created an
 *    event; and every create/update/delete mutation calls `refetchEvents()` so
 *    the server's rows replace the optimistic write. The old fetch-once module
 *    guard (screens stale until manual refresh) is gone.
 *  • Automatic triggers (mount, focus, visibility, poll) are gated: they need
 *    an active subscriber and a visible document, and are skipped while the
 *    last fetch is younger than AUTO_REVALIDATE_FRESH_MS — so a burst of
 *    navigations/focus events costs at most one request per window. Explicit
 *    reload(), refetchEvents(), revalidateEvents(), tenant change and an
 *    upsertEvent() write-through are NOT gated.
 *
 * `upsertEvent` remains a CACHE write-through of an AWAITED server response
 * (per the no-fake-interactivity mandate, callers only reach it after the op
 * resolved) — the server stays canonical: the next revalidation replaces the
 * whole list from `listEvents`.
 */
export interface EventsState {
  events: UIEvent[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

let _events: UIEvent[] | null = null; // last-good rows; null = never loaded
let _loading = true;
/** An explicit Refresh is running over rows that are already on screen. Kept
 *  apart from `_loading`, which owns the never-loaded empty state. */
let _refreshing = false;
let _error: string | null = null;
let _inFlight: Promise<void> | null = null;
let _total: number | null = null; // §2.8 — server-authoritative total (the fetch caps at 500 rows)
let _hasMore = false;
// Freshness window for AUTOMATIC revalidation only. 0 = stale (never fetched,
// purged by a tenant switch, or invalidated by a mutation write-through).
const AUTO_REVALIDATE_FRESH_MS = 30000;
let _lastFetchedAt = 0;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });
// Cross-tenant leak fix, round 2 (docket 2026-07-27, item 1): round 1 (2026-07-26)
// purged AND refetched together, synchronously, inside switchWorkspace — before
// the real session-switch op resolved. Against a real backend that refetch would
// still run under the OUTGOING tenant's session while being labelled with the
// incoming tenant's slug. Purge only on `onTenantSwitching` (a resolved dummy
// promise in `_inFlight` blocks any incidental revalidateEvents() call during
// the pending window); refetch only once `onTenantChange` confirms the session.
onTenantSwitching(() => { _events = null; _loading = true; _error = null; _inFlight = Promise.resolve(); _total = null; _hasMore = false; _lastFetchedAt = 0; _emit(); });
onTenantChange(() => { _inFlight = null; _lastFetchedAt = 0; if (can("events.read")) void revalidateEvents(); }); // admin/events — gated (docket 2026-09-17)

async function _fetch(): Promise<void> {
  try {
    // §2.8 (2026-07-26, completed): follow the server's hasMore and PAGE past
    // 500 — StudioEventsListParams declares page+limit and the response carries
    // total/hasMore. Tenants under 500 events still cost exactly one request.
    // 6-page (3,000-row) safety valve: if ever hit, _hasMore stays true so the
    // UI can say "and more" instead of lying.
    const acc: ReturnType<typeof toUIEvent>[] = [];
    let page = 1;
    let total: number | null = null;
    let hasMore = false;
    for (;;) {
      const res = await fetchEventsList({ page, limit: 500 });
      const batch = ((res.data ?? res.events ?? []) as Parameters<typeof toUIEvent>[0][]).map(toUIEvent);
      acc.push(...batch);
      total = (res as { total?: number }).total ?? null;
      hasMore = !!(res as { hasMore?: boolean }).hasMore;
      if (!hasMore || batch.length === 0 || page >= 6) break;
      page++;
    }
    _events = acc;
    _total = total ?? acc.length;
    _hasMore = hasMore && page >= 6; // residual truncation only (valve hit)
    _error = null;
  } catch (e: unknown) {
    _error = e instanceof Error ? e.message : "Failed to load events.";
    // #800: preserve last-good — never blank a previously-loaded list on a
    // transient error. Only a never-loaded store falls to the error empty state.
    if (_events === null) _events = [];
  } finally {
    // Stamp every completed attempt (success OR failure) so automatic triggers
    // cannot hammer a failing/throttled backend; explicit reload still retries.
    _lastFetchedAt = Date.now();
    _loading = false;
    _inFlight = null;
    _emit();
  }
}

/** Fetch (or join the in-flight fetch of) the canonical events list. Used by
 * mount + focus/visibility/poll: a concurrent caller JOINS the in-flight fetch
 * rather than stacking a second request. */
/** Read-only snapshot of the loaded events (2026-08-14) — for derived views
 * (Landing Pages: every event carries its public page, so that tab derives
 * rows from this store rather than duplicating a fetch). Kicks a revalidate
 * when nothing is loaded yet so a caller polling the snapshot converges. */
export function getEventsSnapshot(): UIEvent[] {
  if (_events === null) void revalidateEvents();
  return _events ?? [];
}

export function revalidateEvents(): Promise<void> {
  if (_inFlight) return _inFlight;
  _inFlight = _fetch();
  return _inFlight;
}

/** Gate for AUTOMATIC triggers (mount, focus, visibilitychange, poll). Skips
 * unless a consumer is subscribed and the document is not hidden, and — once
 * the store has loaded — unless the last fetch is older than the freshness
 * window. A never-loaded store (_events === null) always loads for a mounted
 * consumer. Joins an in-flight fetch via revalidateEvents(). */
function _autoRevalidate(): void {
  if (_subs.size === 0) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden" && _events !== null) return;
  if (_events !== null && _lastFetchedAt !== 0 && Date.now() - _lastFetchedAt < AUTO_REVALIDATE_FRESH_MS) return;
  void revalidateEvents();
}

/** Force a FRESH server fetch, chained AFTER any in-flight one, so a read that
 * follows an awaited create/update/delete is guaranteed to reflect the just-
 * committed write (docket 2026-07-13 §1c). The post-mutation path calls this;
 * the server's rows then replace the optimistic upsertEvent() write, so a
 * locally-cached copy can never win over the server. */
export function refetchEvents(): Promise<void> {
  const run = (): Promise<void> => { _inFlight = _fetch(); return _inFlight; };
  return _inFlight ? _inFlight.then(run, run) : run();
}

// ── Convergence for tabs that stayed open (docket 2026-07-13 §1b) ────────────
// With the fetch-once latch gone, a tab left open must re-pull to see events
// other users/tabs created. Revalidate when the tab regains focus or becomes
// visible again, and on a light 60s poll — all through _autoRevalidate(), so
// only while visible AND at least one list is mounted AND the data is outside
// the freshness window. No fetching when hidden or when nothing consumes the store.
if (typeof window !== "undefined") {
  const onWake = () => { _autoRevalidate(); };
  window.addEventListener("focus", onWake);
  document.addEventListener("visibilitychange", onWake);
  setInterval(() => {
    if (document.visibilityState === "visible") _autoRevalidate();
  }, 60000);
}

/** Cache write-through of an awaited create/update response. Server stays
 * canonical — the next mount's revalidation replaces the list wholesale. */
export function upsertEvent(ev: UIEvent): void {
  const cur = _events ?? [];
  const i = cur.findIndex((x) => x.id === ev.id);
  logUpsert("event", i >= 0 ? (cur[i] as unknown as Record<string, unknown>) : null, ev as unknown as Record<string, unknown>, { name: ev.title });
  _events = i >= 0 ? cur.map((x) => (x.id === ev.id ? ev : x)) : [ev, ...cur];
  // A committed mutation invalidates freshness: the next automatic trigger
  // refetches even if a caller skipped refetchEvents().
  _lastFetchedAt = 0;
  _emit();
}

export function useEvents(): EventsState & { reload: () => Promise<void>; refreshing: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _subs.add(cb);
    // Revalidate on mount (= navigation to a consuming screen):
    // stale-while-revalidate — existing rows render immediately, the fetch
    // refreshes them. Gated by _autoRevalidate(): in-flight dedupe keeps N
    // mounting consumers to 1 request, and the freshness window keeps rapid
    // re-navigation from refetching.
    _autoRevalidate();
    return () => { _subs.delete(cb); };
  }, []);
  return {
    events: _events ?? [],
    total: _total ?? (_events ?? []).length,
    hasMore: _hasMore,
    loading: _events === null ? _loading : false,
    error: _error,
    // Explicit reload: bypasses the freshness gate (joins an in-flight fetch,
    // otherwise fetches now; the tenant-switch dummy in-flight still blocks it).
    /* REFRESH NOW REPORTS ITSELF (2026-09-17, PP). It set `_loading` only when
       the list was empty and never emitted, and `_fetch` does not raise a
       loading flag of its own — so on a populated list the icon fired a real
       request and NOTHING on screen changed: no spinner, no row movement when
       the data was identical. It read as a dead button, and was reported as
       broken. `_refreshing` is a separate flag from `_loading` on purpose:
       `loading` means "nothing to show yet" and owns the empty state, while
       refreshing means "the rows you are looking at are being re-checked" and
       must NOT blank them. */
    refreshing: _refreshing,
    reload: () => {
      _refreshing = true;
      _emit();
      return revalidateEvents().finally(() => { _refreshing = false; _emit(); });
    },
  };
}
